import { InputOTP, InputOTPGroup, InputOTPSlot } from '@chiroup/components/ui/input-otp';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import React, { useEffect, useState } from 'react';

const verbiage = {
  en: {
    eyebrow: 'Verify it’s you',
    title: 'Enter your code',
    body: 'We sent a 5-digit code. Enter it below to continue.',
    resend: 'Send a new code',
    invalid: 'That code didn’t match. Try again or request a new one.',
    expiresIn: 'Expires in',
    expired: 'Code expired',
    codeLabel: 'Verification code',
  },
  es: {
    eyebrow: 'Verifique su identidad',
    title: 'Ingresar el código',
    body: 'Le enviamos un código de 5 dígitos. Ingréselo abajo para continuar.',
    resend: 'Enviar un código nuevo',
    invalid: 'El código no es correcto. Intente otra vez o solicite uno nuevo.',
    expiresIn: 'Expira en',
    expired: 'Código vencido',
    codeLabel: 'Código de verificación',
  },
};

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const r = s % 60;

  return `${m}:${String(r).padStart(2, '0')}`;
};

type Props = {
  authResponse: string;
  onSubmitCode: (code: number) => void | Promise<void>;
  onHandleNewCode: () => void;
  loading?: boolean;
  language?: 'en' | 'es';
  invalidCode?: boolean;
  timeLeft?: number;
  primaryColor?: string;
};

const CODE_LENGTH = 5;

const RequestCodeInput: React.FC<Props> = ({
  authResponse,
  onSubmitCode,
  onHandleNewCode,
  loading,
  language = 'en',
  invalidCode,
  timeLeft = 0,
  primaryColor = '#77bd43',
}) => {
  const v = verbiage[language === 'es' ? 'es' : 'en'];
  const [code, setCode] = useState<string>('');

  // Auto-submit once the user fills (or autofills) the full code.
  useEffect(() => {
    if (code.length === CODE_LENGTH && !loading) {
      onSubmitCode(Number(code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Reset on invalidCode flip so the user can re-enter.
  useEffect(() => {
    if (invalidCode) setCode('');
  }, [invalidCode]);

  return (
    <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="w-full max-w-md">
        <div className="rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
          <div className="p-6 sm:p-8 text-white" style={{ backgroundColor: primaryColor }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
              {v.eyebrow}
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">{v.title}</h1>
            <p className="mt-3 text-sm text-white/90 max-w-prose">{authResponse || v.body}</p>
          </div>

          <div className="p-5 sm:p-6 flex flex-col gap-5 items-center">
            <InputOTP
              maxLength={CODE_LENGTH}
              pattern={REGEXP_ONLY_DIGITS}
              value={code}
              onChange={setCode}
              disabled={loading}
              autoComplete="one-time-code"
              autoFocus
              aria-label={v.codeLabel}
            >
              <InputOTPGroup>
                {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-14 w-12 sm:h-16 sm:w-14 text-2xl sm:text-3xl bg-white dark:bg-white/10"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>

            {invalidCode && <p className="text-sm text-red-600 dark:text-red-400">{v.invalid}</p>}

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onHandleNewCode}
                disabled={loading || timeLeft > 0}
                className="text-sm font-medium hover:underline disabled:opacity-40 disabled:no-underline"
                style={{ color: primaryColor }}
              >
                {v.resend}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {timeLeft > 0
                  ? `${v.expiresIn} ${formatTime(timeLeft)}`
                  : authResponse
                    ? v.expired
                    : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RequestCodeInput;
